/**
 * The small pieces every screen is built from.
 *
 * Sizing is not cosmetic here: `--tap` is 56 px on the airy profile because the
 * target is a twelve-year-old's finger on a tablet, not a cursor (SPEC §11).
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";

import { useAvancement } from "@/api/chargement";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
};

export function Button({ variant = "primary", className, ...rest }: ButtonProps) {
  return <button {...rest} className={`btn btn--${variant} ${className ?? ""}`.trim()} />;
}

export function Screen({
  title,
  lead,
  actions,
  children,
}: {
  title: string;
  lead?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="screen">
      <header className="screen__header">
        <h1 className="screen__title">{title}</h1>
        {actions ? <div className="screen__actions">{actions}</div> : null}
      </header>
      {lead ? <p className="screen__lead">{lead}</p> : null}
      {children}
    </main>
  );
}

export function Card({
  title,
  children,
  tone,
}: {
  title?: string;
  children: ReactNode;
  tone?: "neutral" | "attention" | "good";
}) {
  return (
    <section className={`card card--${tone ?? "neutral"}`}>
      {title ? <h2 className="card__title">{title}</h2> : null}
      {children}
    </section>
  );
}

/**
 * L'attente d'un écran.
 *
 * Le libellé seul ne disait rien de ce qui se passe — *« ça n'a pas l'air
 * d'avancer alors que si »*, Hedi le 25 septembre 2026 —, d'où la piste en
 * dessous, alimentée par ce qui est réellement en vol vers le serveur
 * (`api/chargement`, qui explique ce qui y est mesuré et ce qui y est prédit).
 *
 * Elle **ne s'affiche pas** quand rien n'est en vol : un écran qui attend
 * autre chose qu'une requête — une image qui se décode, un rendu long — garde
 * le libellé seul, et c'est correct. Une piste immobile à cet endroit serait
 * la fausse nouvelle qu'on répare ici.
 */
export function Loading({ label = "Un instant…" }: { label?: string }) {
  const avancement = useAvancement();

  return (
    <p className="loading" role="status">
      {label}
      {avancement > 0 ? (
        <span className="loading__piste" aria-hidden="true">
          <span
            className="loading__part"
            style={{ width: `${Math.round(avancement * 100)}%` }}
          />
        </span>
      ) : null}
    </p>
  );
}

/**
 * An error, in the family's language.
 *
 * The message comes from the server when there is one: `core-api` owns the
 * French wording, so there is a single place where those sentences live.
 */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p className="error-note" role="alert">
      {children}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}
